import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { promptGenerationService } from '../../services/promptGenerationService';
import StatisticsCards from './components/StatisticsCards';
import UsageTrendChart from './components/UsageTrendChart';
import ToolDistributionChart from './components/ToolDistributionChart';
import UseCaseDistributionChart from './components/UseCaseDistributionChart';
import DeviceDemographics from './components/DeviceDemographics';

const UsageAnalyticsDashboard = () => {
  const [stats, setStats] = useState({
    totalPrompts: 0,
    thisWeek: 0,
    today: 0,
    mostPopularTool: 'N/A'
  });
  const [trendData, setTrendData] = useState([]);
  const [toolData, setToolData] = useState([]);
  const [useCaseData, setUseCaseData] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7');

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        analyticsStats,
        usageTrend,
        toolUsage,
        useCaseDistribution,
        deviceDemographics
      ] = await Promise.all([
        promptGenerationService?.getAnalytics(),
        promptGenerationService?.getUsageTrend(parseInt(timeRange)),
        promptGenerationService?.getToolUsage(),
        promptGenerationService?.getUseCaseDistribution(),
        promptGenerationService?.getDeviceDemographics()
      ]);

      setStats(analyticsStats);
      setTrendData(usageTrend);
      setToolData(toolUsage);
      setUseCaseData(useCaseDistribution);
      setDeviceData(deviceDemographics);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Usage Analytics Dashboard</h1>
            <p className="text-muted-foreground">
              Comprehensive insights into prompt generation patterns and user behavior
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e?.target?.value)}
              className="px-4 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="7">Last 7 Days</option>
              <option value="14">Last 14 Days</option>
              <option value="30">Last 30 Days</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={loadAnalyticsData}
              iconName="RefreshCw"
              iconPosition="left"
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-destructive/10 border border-destructive rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-destructive mb-1">Error Loading Analytics</h3>
                <p className="text-sm text-destructive/80">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadAnalyticsData}
                  className="mt-3"
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Retry
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <StatisticsCards stats={stats} />

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Usage Trend Chart */}
          <UsageTrendChart data={trendData} />

          {/* Tool Distribution Chart */}
          <ToolDistributionChart data={toolData} />

          {/* Use Case Distribution Chart */}
          <UseCaseDistributionChart data={useCaseData} />

          {/* Device Demographics */}
          <DeviceDemographics data={deviceData} />
        </div>

        {/* System Information */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Info" size={20} className="text-primary" />
            Analytics Overview
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Total Prompt Generations</span>
              <span className="text-sm font-medium text-foreground">{stats?.totalPrompts}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Most Popular AI Tool</span>
              <span className="text-sm font-medium text-foreground">{stats?.mostPopularTool}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border">
              <span className="text-sm text-muted-foreground">Active Use Cases</span>
              <span className="text-sm font-medium text-foreground">{useCaseData?.length}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">Last Updated</span>
              <span className="text-sm font-medium text-foreground">
                {new Date()?.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageAnalyticsDashboard;